<!-- TurmaCombobox.vue -->
<!-- Combobox do campo "Turma" do cadastro manual de membros — o par do campo
     de mesmo nome da planilha de alunos. Digita/filtra entre os grupos que o
     círculo já tem e aceita texto livre: um nome novo vira grupo na hora do
     envio do lote (ManagedAccountService.LinkBatchItemsToGroupsAsync), não
     aqui. Este componente não cria nada — só escolhe o nome.

     O "Todos" do círculo não aparece: é mantido pelo sistema e o lote recusa
     o nome. Mesmo estilo do PerfilCombobox.vue (construído do zero, sem
     vue-multiselect — ver o cabeçalho de lá). -->
<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from "vue";
import * as groupService from "@/services/groupService";
import { groupNameKey, normalizeGroupName } from "@/utils/memberSheetImport";

const props = defineProps({
  modelValue: { type: String, default: "" },
  placeholder: { type: String, default: "Ex: 9º A, Turma B…" },
  // Como o campo é chamado na tela ("Grupo/Turma" na escola, "Grupo" fora
  // dela) — entra nos textos "Criar …" e "Digite o nome …".
  label: { type: String, default: "grupo" },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue"]);

const ownGroupNames = ref([]);
const isMenuOpen = ref(false);
const dropdownRef = ref(null);
const inputRef = ref(null);

// Filtro separado do modelValue, como no PerfilCombobox: abrir o menu num
// campo já preenchido mostra a lista inteira, não só o que bate com o valor.
const searchQuery = ref("");

onMounted(async () => {
  document.addEventListener("mousedown", handleClickOutside);
  try {
    const response = await groupService.fetchGroups();
    // Um nome por forma normalizada: o círculo pode ter dois "9º A" de
    // criadores diferentes, e o lote só reutiliza o do titular — mostrar os
    // dois seria escolher entre iguais.
    const byKey = new Map();
    for (const g of response.data ?? []) {
      // Grupos de sistema ("Todos", "Alunos", "Professores") não são turma:
      // o lote recusa os nomes e a composição deles vem do perfil.
      if (g.systemRole || g.isCircleDefault || g.createdByUserId == null) continue;
      const name = (g.groupName ?? "").trim();
      const key = groupNameKey(name);
      if (!key || key === "todos" || byKey.has(key)) continue;
      byKey.set(key, name);
    }
    ownGroupNames.value = [...byKey.values()].sort((a, b) =>
      a.localeCompare(b, "pt-BR", { numeric: true })
    );
  } catch (e) {
    console.warn(
      "[TurmaCombobox] Falha ao carregar grupos:",
      e.response?.data?.message || e.message
    );
    ownGroupNames.value = [];
  }
});

onUnmounted(() => {
  document.removeEventListener("mousedown", handleClickOutside);
});

function currentQuery() {
  return searchQuery.value.trim().toLowerCase();
}

// Casa por substring E pela forma normalizada: quem digita "9a" tem que ver
// o "9º A" que já existe — sem isso a lista ficava vazia e o "Criar" também
// não aparecia (o normalizado já existe), como se o campo estivesse quebrado.
const filteredOwn = computed(() => {
  const query = currentQuery();
  if (!query) return ownGroupNames.value;
  const normalizedKey = groupNameKey(searchQuery.value);
  return ownGroupNames.value.filter(
    (name) =>
      name.toLowerCase().includes(query) || groupNameKey(name) === normalizedKey
  );
});

const canCreate = computed(() => {
  const query = normalizeGroupName(searchQuery.value);
  if (!query) return false;
  if (query.toLowerCase() === "todos") return false;
  return !ownGroupNames.value.some((name) => name.toLowerCase() === query.toLowerCase());
});

const flatOptions = computed(() => {
  const list = filteredOwn.value.map((name) => ({ type: "own", name }));
  if (canCreate.value) {
    list.push({ type: "create", name: normalizeGroupName(searchQuery.value) });
  }
  return list;
});

const highlightedIndex = ref(-1);

function scrollHighlightedIntoView() {
  nextTick(() => {
    dropdownRef.value
      ?.querySelector(".turma-option--highlighted")
      ?.scrollIntoView({ block: "nearest" });
  });
}

function moveHighlight(delta) {
  if (props.disabled) return;
  if (!isMenuOpen.value) {
    openMenu();
    return;
  }
  const total = flatOptions.value.length;
  if (!total) return;
  highlightedIndex.value =
    highlightedIndex.value === -1
      ? delta > 0 ? 0 : total - 1
      : (highlightedIndex.value + delta + total) % total;
  scrollHighlightedIntoView();
}

function confirmHighlighted() {
  const option = flatOptions.value[highlightedIndex.value];
  if (!option) return;
  selectOption(option.name);
}

function handleKeydown(event) {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    moveHighlight(1);
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    moveHighlight(-1);
  } else if (event.key === "Enter") {
    if (!isMenuOpen.value || highlightedIndex.value === -1) return;
    event.preventDefault();
    confirmHighlighted();
  } else if (event.key === "Escape") {
    isMenuOpen.value = false;
    highlightedIndex.value = -1;
  }
}

function openMenu() {
  if (props.disabled) return;
  searchQuery.value = "";
  isMenuOpen.value = true;
  highlightedIndex.value = -1;
}

function focusInput() {
  inputRef.value?.focus();
  openMenu();
}

function handleInput(event) {
  emit("update:modelValue", event.target.value);
  searchQuery.value = event.target.value;
  highlightedIndex.value = -1;
}

function selectOption(name) {
  const trimmed = normalizeGroupName(name);
  if (!trimmed) return;
  emit("update:modelValue", trimmed);
  searchQuery.value = "";
  isMenuOpen.value = false;
  highlightedIndex.value = -1;
}

function handleClickOutside(event) {
  if (isMenuOpen.value && !event.target.closest(".turma-dropdown")) {
    isMenuOpen.value = false;
    highlightedIndex.value = -1;
  }
}
</script>

<template>
  <div ref="dropdownRef" class="turma-dropdown">
    <div
      class="turma-trigger"
      :class="{ active: isMenuOpen, 'turma-trigger--disabled': disabled }"
    >
      <input
        ref="inputRef"
        :value="modelValue"
        type="text"
        class="turma-trigger__input"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="off"
        @input="handleInput"
        @focus="openMenu"
        @keydown="handleKeydown"
      />
      <i
        class="fas fa-chevron-down turma-trigger__chevron"
        :class="{ open: isMenuOpen }"
        @mousedown.prevent="focusInput"
      ></i>
    </div>

    <div v-if="isMenuOpen" class="turma-menu">
      <ul>
        <li v-if="filteredOwn.length > 0" class="turma-group-label">Grupos existentes</li>
        <li
          v-for="(option, index) in flatOptions"
          :key="option.type + '-' + option.name"
          class="turma-option"
          :class="{
 'turma-option--create': option.type === 'create',
            'turma-option--highlighted': index === highlightedIndex,
          }"
          @mousedown.prevent="selectOption(option.name)"
          @mouseenter="highlightedIndex = index"
        >
          {{ option.type === "create" ? `Criar ${label.toLowerCase()} "${option.name}"` : option.name }}
        </li>

        <li v-if="!flatOptions.length" class="turma-option turma-option--empty">
          Digite o nome do {{ label.toLowerCase() }}
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.turma-dropdown {
  position: relative;
}

.turma-trigger {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  height: 32px;
  padding: 0 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--surface);
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.turma-trigger:hover {
  border-color: var(--focus-border);
  background-color: #f5f8ff;
}
.turma-trigger.active {
  border-color: var(--focus-border);
}
.turma-trigger--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.turma-trigger--disabled:hover {
  border-color: var(--border-color);
  background-color: var(--surface);
}

.turma-trigger__input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-dark);
}
/* Mesmo reforço do PerfilCombobox: o anel de foco do navegador passa por
   cima de um outline:none comum. */
.turma-trigger__input:focus,
.turma-trigger__input:focus-visible {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
}
.turma-trigger__input::placeholder {
  font-weight: 400;
}

.turma-trigger__chevron {
  flex-shrink: 0;
  font-size: 0.6rem;
  color: var(--text-muted);
  opacity: 0.7;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}
.turma-trigger__chevron.open {
  transform: rotate(180deg);
}

.turma-menu {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 100;
  max-height: 8.5rem;
  overflow-y: auto;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
}
.turma-menu ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}

.turma-group-label {
  padding: 0.35rem 0.7rem 0.15rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-subtle);
}

.turma-option {
  padding: 0.45rem 0.7rem;
  border-radius: 7px;
  font-size: 0.85rem;
  color: var(--text-dark);
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background-color 0.2s ease-in-out;
}
.turma-option:hover,
.turma-option--highlighted {
  background-color: var(--brand-tint);
  color: var(--text-brand);
}
.turma-option--create {
  color: var(--text-brand);
  font-weight: 600;
}
.turma-option--empty {
  color: var(--text-subtle);
  cursor: default;
}
.turma-option--empty:hover {
  background-color: transparent;
  color: var(--text-subtle);
}
</style>
